/**
 * オペレーターの登録・編集ポップアップ(NEXUS Gate のマスタと同じ作り)。
 * - 項目は 2026-09-09 のスタッフ項目表。枠内は「出勤形態／報酬区分／BB ID／内線番号／時給or単価」の順
 * - 出勤形態と報酬区分を選ぶと、BB ID(形態コード＋連番)と内線番号(連番の数字)が自動生成・自動入力される。
 *   タイミーは報酬区分が時給のみ
 * - 報酬は **BB ID単位**(③指示)。変更は「報酬の改訂」でBB IDを指定して行い、そのBB IDの改訂履歴に残る
 * - 枠の並びは 出勤形態／報酬区分／架電内容／時給・件数単価／BB ID／内線番号、架電会社は上段(在籍の次)に1つ(⑨指示)
 * - 交通費(往復)も改訂欄から変更(③指示)
 * - 登録日・最終更新・変更履歴(監査ログ)を表示する
 * - 出勤形態・報酬区分・架電内容は MTG で確定したコード(M/A/H/T・H/C・A/B)を名前の前に出す(2026-09-17)
 * - インボイス控除(在宅のみ)・PCレンタル・成績表の公開 を持つ(2026-09-17 MTG。インボイス控除は区分だけで計算には使わない)
 */
import { useEffect, useMemo, useState, type FormEvent } from 'react';
import Modal from '@core/components/Modal';
import NumInput from '@core/components/NumInput';
import { useToast } from '@core/components/Toast';
import { useAuth } from '@core/auth/AuthProvider';
import { auditText, fetchAuditLogs, fmtTs, diffLines } from '@core/lib/auditLog';
import { todayStr, toDateLabel } from '@core/lib/date';
import { formatYen, parseAmount } from '@core/lib/money';
import {
  BUSINESS_CODES,
  BUSINESS_LABELS,
  DEFAULT_BB_NUMBERING,
  INVOICE_TYPES,
  INVOICE_TYPE_LABELS,
  OPERATOR_STATUSES,
  OPERATOR_STATUS_LABELS,
  PAY_TYPES,
  PAY_TYPE_CODES,
  PAY_TYPE_LABELS,
  WORK_STYLES,
  WORK_STYLE_CODES,
  WORK_STYLE_CODE_LABELS,
  styleLabel,
  wageLabel,
  type AuditLog,
  type BbAccount,
  type BbNumberingSettings,
  type BusinessType,
  type Company,
  type ExtraInfo,
  type InvoiceType,
  type Operator,
  type OperatorStatus,
  type PayType,
  type UserDoc,
  type WorkStyle,
} from '@core/lib/types';
import {
  createOperator,
  deleteOperator,
  updateOperator,
  type CommuteRevisionInput,
  type OperatorInput,
  type WageRevisionInput,
} from './operatorRepo';
import { extensionOf, fetchBbNumbering, generateBbId, normalizePayType, usedBbIds } from './bbNumbering';
import { fetchUsers } from './userRepo';

/** 入力中のBB ID行。auto=true の間は出勤形態・報酬区分に応じてBB ID・内線を自動で入れ直す。wageText は新規行の初期報酬 */
type AccountRow = BbAccount & { auto: boolean; isNew: boolean; wageText: string };

/** 「改訂を追加」の1行(入力中) */
type RevisionRow = { bb_id: string; effective_from: string; wageText: string; reason: string };
type CommuteRow = { effective_from: string; amountText: string; reason: string };

const inputStyle = { width: '100%', padding: '6px 8px', border: '1px solid var(--line)', borderRadius: 6, fontFamily: 'inherit', fontSize: 13 } as const;

function newRow(base?: Partial<AccountRow>): AccountRow {
  return {
    work_style: 'shinagawa',
    pay_type: 'hourly',
    bb_id: '',
    ext: '',
    company_id: null,
    business: 'kyutoki',
    active: true,
    current_wage: 0,
    wage_history: [],
    auto: true,
    isNew: true,
    wageText: '',
    ...base,
  };
}

export default function OperatorEditModal({
  operator,
  operators,
  companies,
  onClose,
  onSaved,
}: {
  operator: Operator | null; // null=新規登録
  /** 使用済みBB IDの判定に使う */
  operators: Operator[];
  companies: Company[];
  onClose: () => void;
  onSaved: () => void;
}) {
  const toast = useToast();
  const { user } = useAuth();
  const isNew = operator === null;

  const [name, setName] = useState(operator?.name ?? '');
  const [accounts, setAccounts] = useState<AccountRow[]>(
    operator?.bb_accounts.length
      ? operator.bb_accounts.map((a) => ({
          ...a,
          ext: a.ext ?? '',
          work_style: a.work_style ?? operator.work_style,
          pay_type: a.pay_type ?? operator.pay_type,
          current_wage: a.current_wage ?? 0,
          wage_history: a.wage_history ?? [],
          auto: false,
          isNew: false,
          wageText: String(a.current_wage ?? 0),
        }))
      : [newRow()],
  );
  const [status, setStatus] = useState<OperatorStatus>(operator?.status ?? 'active');
  const [commute, setCommute] = useState(operator ? String(operator.commute_cost) : '0');
  const [joinedAt, setJoinedAt] = useState(operator?.joined_at ?? '');
  const [leftAt, setLeftAt] = useState(operator?.left_at ?? '');
  const [extra, setExtra] = useState<ExtraInfo[]>(operator?.extra_info?.length ? operator.extra_info : []);
  const [note, setNote] = useState(operator?.note ?? '');
  const [invoiceType, setInvoiceType] = useState<InvoiceType | ''>(operator?.invoice_type ?? '');
  const [pcRental, setPcRental] = useState(operator?.pc_rental === true);
  const [statsVisible, setStatsVisible] = useState(operator?.stats_visible === true);
  const [revisions, setRevisions] = useState<RevisionRow[]>([]);
  const [commuteRevisions, setCommuteRevisions] = useState<CommuteRow[]>([]);
  const [busy, setBusy] = useState(false);
  const [confirmDelete, setConfirmDelete] = useState(false);

  const [numbering, setNumbering] = useState<BbNumberingSettings | null>(null);
  const [history, setHistory] = useState<AuditLog[] | null>(null);
  const [historyError, setHistoryError] = useState(false);
  const [userNames, setUserNames] = useState<Map<string, string>>(new Map());
  const nameOf = (id: string | null | undefined) => (id ? (userNames.get(id) ?? id) : '—');

  useEffect(() => {
    void fetchBbNumbering()
      .then(setNumbering)
      .catch(() => {
        toast('採番ルールを読み込めなかったため、既定のルールでBB IDを生成します');
        setNumbering(DEFAULT_BB_NUMBERING);
      });
    void fetchUsers()
      .then((list: UserDoc[]) => setUserNames(new Map(list.map((u) => [u.id, u.name]))))
      .catch(() => undefined); // 登録者名の表示用。失敗時はIDのまま出す
    if (operator) {
      void fetchAuditLogs('operator', operator.id)
        .then(setHistory)
        .catch(() => {
          setHistory([]);
          setHistoryError(true);
        });
    }
  }, [operator]);

  /** 他のオペレーターが使っているBB ID(自分自身の分は除く) */
  const usedByOthers = useMemo(() => usedBbIds(operators.filter((o) => o.id !== operator?.id)), [operators, operator]);

  /** 自動行のBB ID・内線を、その行の出勤形態・報酬区分・架電内容で入れ直す */
  function regenerate(rows: AccountRow[], settings: BbNumberingSettings): AccountRow[] {
    const used = new Set(usedByOthers);
    for (const r of rows) if (!r.auto && r.bb_id.trim()) used.add(r.bb_id.trim().toLowerCase());
    return rows.map((r) => {
      if (!r.auto) return r;
      const payType = normalizePayType(r.work_style, r.pay_type);
      const bbId = generateBbId(r.work_style, payType, r.business, settings, used);
      return { ...r, pay_type: payType, bb_id: bbId, ext: extensionOf(bbId) ?? '' };
    });
  }

  useEffect(() => {
    if (!numbering) return;
    setAccounts((prev) => (prev.some((r) => r.auto) ? regenerate(prev, numbering) : prev));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [numbering]);

  function setAccount(i: number, patch: Partial<AccountRow>) {
    setAccounts((prev) => {
      const next = prev.map((a, j) => (j === i ? { ...a, ...patch } : a));
      const styleChanged = 'work_style' in patch || 'pay_type' in patch || 'business' in patch;
      return styleChanged && numbering ? regenerate(next, numbering) : next;
    });
  }

  function addRow() {
    setAccounts((prev) => {
      const last = prev[prev.length - 1];
      const row = newRow(last ? { work_style: last.work_style, pay_type: last.pay_type, company_id: last.company_id, business: last.business } : {});
      return numbering ? regenerate([...prev, row], numbering) : [...prev, row];
    });
  }

  const hasShinagawa = accounts.some((a) => a.work_style === 'shinagawa' || a.work_style === 'timee');
  /** インボイス控除は在宅スタッフのみ */
  const hasZaitaku = accounts.some((a) => a.work_style === 'zaitaku');
  const accountLabel = (a: Pick<BbAccount, 'bb_id' | 'work_style' | 'pay_type'>) => `${a.bb_id}(${styleLabel(a.work_style, normalizePayType(a.work_style, a.pay_type))})`;
  const payTypeOf = (bbId: string): PayType => {
    const a = accounts.find((x) => x.bb_id === bbId);
    return a ? normalizePayType(a.work_style, a.pay_type) : 'hourly';
  };

  /** 変更内容を「項目: 旧 → 新」の文章にする(変更履歴に残す) */
  function describeChanges(current: Operator, input: OperatorInput, revs: WageRevisionInput[], commuteRevs: CommuteRevisionInput[]): string[] {
    const { pair, lines } = diffLines();
    const companyName = (id: string | null) => (id ? (companies.find((c) => c.id === id)?.name ?? id) : '—');
    const accountText = (list: BbAccount[]) =>
      list
        .filter((a) => a.bb_id.trim())
        .map((a) => `${accountLabel(a)}/内線${a.ext || '—'}/${companyName(a.company_id)}/${BUSINESS_LABELS[a.business]}${a.active ? '' : '/無効'}`)
        .join('、');
    pair('名前', current.name, input.name.trim());
    pair('BB ID', accountText(current.bb_accounts), accountText(input.bb_accounts));
    pair('在籍', OPERATOR_STATUS_LABELS[current.status], OPERATOR_STATUS_LABELS[input.status]);
    pair('入社日', toDateLabel(current.joined_at), toDateLabel(input.joined_at));
    pair('退職日', toDateLabel(current.left_at), toDateLabel(input.left_at));
    const extraText = (list: ExtraInfo[]) => list.map((e) => `${e.label}=${e.value}`).join('、');
    pair('その他情報', extraText(current.extra_info ?? []), extraText(input.extra_info));
    pair('備考', current.note ?? '', input.note.trim());
    const invoiceText = (v: InvoiceType | null | undefined) => (v ? INVOICE_TYPE_LABELS[v] : '未設定');
    pair('インボイス控除', invoiceText(current.invoice_type), invoiceText(input.invoice_type));
    pair('PCレンタル', current.pc_rental ? 'あり' : 'なし', input.pc_rental ? 'あり' : 'なし');
    pair('成績表の公開', current.stats_visible ? '公開する' : '公開しない', input.stats_visible ? '公開する' : '公開しない');
    for (const a of input.bb_accounts) {
      if (a.bb_id && !current.bb_accounts.some((c) => c.bb_id === a.bb_id)) lines.push(`BB IDを追加: ${accountLabel(a)} ${wageLabel(a.pay_type)} ${formatYen(a.current_wage)}円`);
    }
    for (const r of revs) lines.push(`報酬の改訂: ${r.bb_id} ${toDateLabel(r.effective_from)}から ${wageLabel(payTypeOf(r.bb_id))} ${formatYen(r.wage)}円(${r.reason})`);
    for (const r of commuteRevs) lines.push(`交通費の改訂: ${toDateLabel(r.effective_from)}から ${formatYen(r.amount)}円(${r.reason})`);
    return lines;
  }

  function parseRevisions(): WageRevisionInput[] {
    return revisions.map((r) => {
      const w = parseAmount(r.wageText);
      if (w === null) throw new Error('改訂後の金額は0以上の整数(円)で入力してください');
      return { bb_id: r.bb_id, effective_from: r.effective_from, wage: w, reason: r.reason };
    });
  }

  function parseCommuteRevisions(): CommuteRevisionInput[] {
    return commuteRevisions.map((r) => {
      const a = parseAmount(r.amountText);
      if (a === null) throw new Error('改訂後の交通費は0以上の整数(円)で入力してください');
      return { effective_from: r.effective_from, amount: a, reason: r.reason };
    });
  }

  function toAccounts(): BbAccount[] {
    return accounts.map(({ auto: _auto, isNew: rowIsNew, wageText, ...a }) => {
      let currentWage = a.current_wage;
      if (rowIsNew) {
        const w = parseAmount(wageText || '0');
        if (w === null) throw new Error(`${a.bb_id || 'BB ID'} の${wageLabel(a.pay_type)}は0以上の整数(円)で入力してください`);
        currentWage = w;
      }
      return { ...a, pay_type: normalizePayType(a.work_style, a.pay_type), current_wage: currentWage };
    });
  }

  async function handleSubmit(e: FormEvent) {
    e.preventDefault();
    if (!user) return;
    setBusy(true);
    try {
      const revs = parseRevisions();
      const commuteRevs = parseCommuteRevisions();
      const bbAccounts = toAccounts();
      const common = {
        name,
        bb_accounts: bbAccounts,
        status,
        joined_at: joinedAt || null,
        left_at: leftAt || null,
        extra_info: extra,
        note,
        invoice_type: hasZaitaku && invoiceType ? invoiceType : null,
        pc_rental: pcRental,
        stats_visible: statsVisible,
      };
      if (isNew) {
        const commuteNum = parseAmount(commute || '0');
        if (commuteNum === null) throw new Error('交通費は0以上の整数(円)で入力してください');
        await createOperator({ ...common, commute_cost: hasShinagawa ? commuteNum : 0 }, user.id, { revisions: revs, commuteRevisions: commuteRevs });
        toast('オペレーターを登録しました');
      } else {
        const input: OperatorInput = { ...common, commute_cost: operator.commute_cost };
        await updateOperator({
          current: operator,
          input,
          revisions: revs,
          commuteRevisions: commuteRevs,
          changes: describeChanges(operator, input, revs, commuteRevs),
          userId: user.id,
        });
        toast('保存しました');
      }
      onSaved();
    } catch (err) {
      toast(err instanceof Error ? err.message : '保存に失敗しました');
    } finally {
      setBusy(false);
    }
  }

  async function handleDelete() {
    if (!operator || !user) return;
    setBusy(true);
    try {
      await deleteOperator(operator, user.id);
      toast('削除しました');
      onSaved();
    } catch (err) {
      toast(err instanceof Error ? err.message : '削除に失敗しました');
    } finally {
      setBusy(false);
    }
  }

  /** 全BB IDの改訂履歴(新しい順) */
  const wageHistoryRows = useMemo(
    () =>
      (operator?.bb_accounts ?? [])
        .flatMap((a) => (a.wage_history ?? []).map((h) => ({ ...h, bb_id: a.bb_id, pay_type: a.pay_type })))
        .sort((x, y) => (x.effective_from < y.effective_from ? 1 : x.effective_from > y.effective_from ? -1 : 0)),
    [operator],
  );
  const today = todayStr();
  const upcomingWage = wageHistoryRows.filter((h) => h.effective_from > today);
  const commuteHistoryRows = [...(operator?.commute_history ?? [])].sort((x, y) => (x.effective_from < y.effective_from ? 1 : -1));
  const selectableAccounts = accounts.filter((a) => a.bb_id.trim());

  return (
    <Modal onClose={onClose} width="min(920px, 94vw)">
      <h3>{isNew ? 'オペレーターの新規登録' : `${operator.name}(${operator.op_code})の編集`}</h3>
      {operator && (
        <p className="hint" style={{ margin: '4px 0 0' }}>
          登録日: {fmtTs(operator.created_at)} {nameOf(operator.created_by)} ／ 最終更新: {fmtTs(operator.updated_at)} {nameOf(operator.updated_by)}
        </p>
      )}

      <form onSubmit={handleSubmit}>
        <div className="form-grid" style={{ marginTop: 14, gridTemplateColumns: 'repeat(2, minmax(0, 1fr))' }}>
          <div>
            <label>管理番号</label>
            <input readOnly disabled value={operator?.op_code ?? 'システム自動付与'} style={{ background: '#EEF2F8', color: 'var(--muted)' }} />
          </div>
          <div>
            <label>名前 *</label>
            <input value={name} onChange={(e) => setName(e.target.value)} required autoFocus={isNew} />
          </div>
          <div>
            <label>在籍</label>
            <select value={status} onChange={(e) => setStatus(e.target.value as OperatorStatus)}>
              {OPERATOR_STATUSES.map((s) => (
                <option key={s} value={s}>
                  {OPERATOR_STATUS_LABELS[s]}
                </option>
              ))}
            </select>
          </div>
          <div>
            <label>架電会社</label>
            {/* 架電会社は人ごとに1つ(⑨指示で上段へ)。全BB IDの割振先として保存する */}
            <select
              value={accounts[0]?.company_id ?? ''}
              onChange={(e) => {
                const cid = e.target.value || null;
                setAccounts((prev) => prev.map((a) => ({ ...a, company_id: cid })));
              }}
            >
              <option value="">—</option>
              {companies.map((c) => (
                <option key={c.id} value={c.id}>
                  {c.name}
                </option>
              ))}
            </select>
          </div>
          <div>
            <label>入社日</label>
            <input type="date" value={joinedAt} onChange={(e) => setJoinedAt(e.target.value)} />
          </div>
          <div>
            <label>退職日</label>
            <input type="date" value={leftAt} onChange={(e) => setLeftAt(e.target.value)} />
          </div>
          <div>
            <label>インボイス控除(在宅のみ)</label>
            <select
              value={hasZaitaku ? invoiceType : ''}
              disabled={!hasZaitaku}
              title={hasZaitaku ? undefined : '在宅のBB IDがある人だけ設定できます'}
              onChange={(e) => setInvoiceType(e.target.value as InvoiceType | '')}
            >
              <option value="">{hasZaitaku ? '未設定' : '—'}</option>
              {INVOICE_TYPES.map((t) => (
                <option key={t} value={t}>
                  {INVOICE_TYPE_LABELS[t]}
                </option>
              ))}
            </select>
          </div>
          <div>
            <label>PCレンタル</label>
            <select value={pcRental ? '1' : '0'} onChange={(e) => setPcRental(e.target.value === '1')}>
              <option value="0">なし</option>
              <option value="1">あり</option>
            </select>
          </div>
          <div>
            <label>成績表の公開(本人)</label>
            <select value={statsVisible ? '1' : '0'} onChange={(e) => setStatsVisible(e.target.value === '1')}>
              <option value="0">公開しない</option>
              <option value="1">公開する</option>
            </select>
          </div>
          <div>
            <label>備考</label>
            <input value={note} onChange={(e) => setNote(e.target.value)} />
          </div>
        </div>

        {/* ---- 出勤形態／報酬区分／BB ID／内線番号／報酬(複数) ---- */}
        <div className="gray-box">
          <div className="box-title">
            出勤形態・報酬区分・架電内容・報酬・BB ID・内線番号
            <span className="hint" style={{ fontWeight: 400, marginLeft: 8 }}>出勤形態・報酬区分・架電内容からBB ID・内線番号を自動採番。報酬の変更は「報酬の改訂」から</span>
          </div>
          <table style={{ fontSize: 13 }}>
            <thead>
              <tr>
                <th>出勤形態</th>
                <th>報酬区分</th>
                <th>架電内容</th>
                <th>時給／件数単価</th>
                <th>BB ID</th>
                <th>内線番号</th>
                <th>有効</th>
                <th></th>
              </tr>
            </thead>
            <tbody>
              {accounts.map((a, i) => (
                <tr key={i}>
                  <td>
                    <select value={a.work_style} onChange={(e) => setAccount(i, { work_style: e.target.value as WorkStyle })} style={inputStyle}>
                      {WORK_STYLES.map((w) => (
                        <option key={w} value={w}>
                          {WORK_STYLE_CODES[w]} {WORK_STYLE_CODE_LABELS[w]}
                        </option>
                      ))}
                    </select>
                  </td>
                  <td>
                    <select
                      value={normalizePayType(a.work_style, a.pay_type)}
                      disabled={a.work_style === 'timee'}
                      title={a.work_style === 'timee' ? 'タイミーは時給のみ' : undefined}
                      onChange={(e) => setAccount(i, { pay_type: e.target.value as PayType })}
                      style={inputStyle}
                    >
                      {PAY_TYPES.map((p) => (
                        <option key={p} value={p}>
                          {PAY_TYPE_CODES[p]} {PAY_TYPE_LABELS[p]}
                        </option>
                      ))}
                    </select>
                  </td>
                  <td>
                    <select value={a.business} onChange={(e) => setAccount(i, { business: e.target.value as BusinessType })} style={inputStyle}>
                      {(Object.entries(BUSINESS_LABELS) as [BusinessType, string][]).map(([k, v]) => (
                        <option key={k} value={k}>
                          {BUSINESS_CODES[k]} {v}
                        </option>
                      ))}
                    </select>
                  </td>
                  <td>
                    {a.isNew ? (
                      <NumInput
                        value={a.wageText}
                        onChange={(v) => setAccount(i, { wageText: v })}
                        placeholder={wageLabel(normalizePayType(a.work_style, a.pay_type))}
                        style={{ ...inputStyle, width: 110 }}
                        required
                      />
                    ) : (
                      <span title={`${wageLabel(a.pay_type)}(変更は「報酬の改訂」から)`} style={{ fontWeight: 700 }}>
                        {formatYen(a.current_wage)}円
                      </span>
                    )}
                  </td>
                  <td>
                    <input
                      value={a.bb_id}
                      onChange={(e) => setAccount(i, { bb_id: e.target.value, auto: false })}
                      placeholder={numbering ? '' : '生成中…'}
                      style={{ ...inputStyle, fontWeight: 700 }}
                    />
                    {!a.auto && a.isNew && (
                      <button type="button" className="txn-link" style={{ fontSize: 11, padding: '2px 4px' }} onClick={() => setAccount(i, { auto: true, bb_id: '', ext: '' })}>
                        自動生成に戻す
                      </button>
                    )}
                  </td>
                  <td>
                    <input value={a.ext ?? ''} onChange={(e) => setAccount(i, { ext: e.target.value })} style={{ ...inputStyle, width: 80 }} />
                  </td>
                  <td style={{ textAlign: 'center' }}>
                    <input type="checkbox" checked={a.active} onChange={(e) => setAccount(i, { active: e.target.checked })} />
                  </td>
                  <td>
                    {accounts.length > 1 && (
                      <button type="button" className="txn-link" onClick={() => setAccounts((prev) => prev.filter((_, j) => j !== i))}>
                        −
                      </button>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          <button type="button" className="txn-link" style={{ marginTop: 6 }} onClick={addRow}>
            ＋ BB IDを追加(在宅用・不用品用など)
          </button>
        </div>

        {/* ---- 報酬の改訂(BB IDごと) ---- */}
        <div className="gray-box">
          <div className="box-title">
            報酬(時給／件数単価)の改訂履歴
            <span className="hint" style={{ fontWeight: 400, marginLeft: 8 }}>BB IDごとに持ちます。昇格・降格は「報酬の改訂」から(過去月の再計算時は当時の金額を適用)</span>
          </div>
          {wageHistoryRows.length > 0 && (
            <table style={{ fontSize: 12.5, marginBottom: 8 }}>
              <thead>
                <tr>
                  <th>BB ID</th>
                  <th>適用開始日</th>
                  <th className="num">金額</th>
                  <th>理由</th>
                  <th>登録者</th>
                  <th>登録日時</th>
                </tr>
              </thead>
              <tbody>
                {wageHistoryRows.map((h, i) => (
                  <tr key={i} style={h.effective_from > today ? { color: 'var(--blue)' } : undefined}>
                    <td>{h.bb_id}</td>
                    <td>
                      {toDateLabel(h.effective_from)}
                      {h.effective_from > today && <span className="hint">(予定)</span>}
                    </td>
                    <td className="num">
                      {formatYen(h.wage)}円<span className="hint">({wageLabel(h.pay_type)})</span>
                    </td>
                    <td>{h.reason}</td>
                    <td>{nameOf(h.changed_by)}</td>
                    <td>{fmtTs(h.changed_at)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
          {upcomingWage.length > 0 && <div className="hint" style={{ marginBottom: 6 }}>次回改訂の予定があります(青字)</div>}
          {revisions.map((r, i) => (
            <div key={i} style={{ display: 'flex', gap: 8, alignItems: 'center', marginBottom: 6, flexWrap: 'wrap' }}>
              <select
                value={r.bb_id}
                onChange={(e) => setRevisions((prev) => prev.map((x, j) => (j === i ? { ...x, bb_id: e.target.value } : x)))}
                style={{ ...inputStyle, flex: '0 1 220px' }}
              >
                <option value="">BB IDを選択</option>
                {selectableAccounts.map((a) => (
                  <option key={a.bb_id} value={a.bb_id}>
                    {accountLabel(a)}
                  </option>
                ))}
              </select>
              <input
                type="date"
                value={r.effective_from}
                onChange={(e) => setRevisions((prev) => prev.map((x, j) => (j === i ? { ...x, effective_from: e.target.value } : x)))}
                style={{ ...inputStyle, flex: '0 1 150px' }}
              />
              <NumInput
                placeholder={`改訂後の${wageLabel(payTypeOf(r.bb_id))}(円)`}
                value={r.wageText}
                onChange={(v) => setRevisions((prev) => prev.map((x, j) => (j === i ? { ...x, wageText: v } : x)))}
                style={{ ...inputStyle, flex: '0 1 150px' }}
              />
              <input
                placeholder="理由(昇格・降格・条件変更など)"
                value={r.reason}
                onChange={(e) => setRevisions((prev) => prev.map((x, j) => (j === i ? { ...x, reason: e.target.value } : x)))}
                style={{ ...inputStyle, flex: '1 1 180px' }}
              />
              <button type="button" className="txn-link" onClick={() => setRevisions((prev) => prev.filter((_, j) => j !== i))}>
                取消
              </button>
            </div>
          ))}
          <button
            type="button"
            className="btn-line"
            style={{ padding: '6px 14px' }}
            onClick={() =>
              setRevisions((prev) => [...prev, { bb_id: selectableAccounts[0]?.bb_id ?? '', effective_from: todayStr(), wageText: '', reason: '' }])
            }
          >
            報酬の改訂
          </button>
        </div>

        {/* ---- 交通費(往復)の改訂 ---- */}
        {hasShinagawa && (
          <div className="gray-box">
            <div className="box-title">
              交通費(往復)の改訂履歴
              <span className="hint" style={{ fontWeight: 400, marginLeft: 8 }}>出勤の稼働に応じて支給します</span>
            </div>
            {isNew ? (
              <div className="form-grid" style={{ maxWidth: 260, marginBottom: 8 }}>
                <div>
                  <label>交通費往復(円)</label>
                  <NumInput value={commute} onChange={setCommute} />
                </div>
              </div>
            ) : (
              <>
                <div style={{ marginBottom: 8 }}>
                  現在: <b>{formatYen(operator.commute_cost)}円</b>
                </div>
                {commuteHistoryRows.length > 0 && (
                  <table style={{ fontSize: 12.5, marginBottom: 8 }}>
                    <thead>
                      <tr>
                        <th>適用開始日</th>
                        <th className="num">金額</th>
                        <th>理由</th>
                        <th>登録者</th>
                        <th>登録日時</th>
                      </tr>
                    </thead>
                    <tbody>
                      {commuteHistoryRows.map((h, i) => (
                        <tr key={i} style={h.effective_from > today ? { color: 'var(--blue)' } : undefined}>
                          <td>
                            {toDateLabel(h.effective_from)}
                            {h.effective_from > today && <span className="hint">(予定)</span>}
                          </td>
                          <td className="num">{formatYen(h.amount)}円</td>
                          <td>{h.reason}</td>
                          <td>{nameOf(h.changed_by)}</td>
                          <td>{fmtTs(h.changed_at)}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                )}
              </>
            )}
            {/* 「交通費の改訂」を押すと 改定日・金額・備考 の3項目が出る(⑨指示) */}
            {commuteRevisions.map((r, i) => (
              <div key={i} style={{ display: 'flex', gap: 8, alignItems: 'flex-end', marginBottom: 6, flexWrap: 'wrap' }}>
                <div style={{ flex: '0 1 150px' }}>
                  <label style={{ fontSize: 11.5, color: 'var(--muted)' }}>改定日</label>
                  <input
                    type="date"
                    value={r.effective_from}
                    onChange={(e) => setCommuteRevisions((prev) => prev.map((x, j) => (j === i ? { ...x, effective_from: e.target.value } : x)))}
                    style={inputStyle}
                  />
                </div>
                <div style={{ flex: '0 1 150px' }}>
                  <label style={{ fontSize: 11.5, color: 'var(--muted)' }}>金額(円・往復)</label>
                  <NumInput
                    value={r.amountText}
                    onChange={(v) => setCommuteRevisions((prev) => prev.map((x, j) => (j === i ? { ...x, amountText: v } : x)))}
                    style={inputStyle}
                  />
                </div>
                <div style={{ flex: '1 1 200px' }}>
                  <label style={{ fontSize: 11.5, color: 'var(--muted)' }}>備考</label>
                  <input
                    placeholder="引越し・定期区間変更など"
                    value={r.reason}
                    onChange={(e) => setCommuteRevisions((prev) => prev.map((x, j) => (j === i ? { ...x, reason: e.target.value } : x)))}
                    style={inputStyle}
                  />
                </div>
                <button type="button" className="txn-link" style={{ marginBottom: 6 }} onClick={() => setCommuteRevisions((prev) => prev.filter((_, j) => j !== i))}>
                  取消
                </button>
              </div>
            ))}
            <button
              type="button"
              className="btn-line"
              style={{ padding: '6px 14px' }}
              onClick={() => setCommuteRevisions((prev) => [...prev, { effective_from: todayStr(), amountText: '', reason: '' }])}
            >
              交通費の改訂
            </button>
          </div>
        )}

        {/* ---- その他情報(Googleフォームの情報。＋／−) ---- */}
        <div className="gray-box">
          <div className="box-title">その他情報(Googleフォームの情報など)</div>
          {extra.length === 0 && <div className="hint" style={{ marginBottom: 6 }}>項目がありません。「＋ 項目を追加」で追加できます</div>}
          {extra.map((e, i) => (
            <div key={i} style={{ display: 'flex', gap: 8, alignItems: 'center', marginBottom: 6, flexWrap: 'wrap' }}>
              <input
                placeholder="項目名(例: 住所)"
                value={e.label}
                onChange={(ev) => setExtra((prev) => prev.map((x, j) => (j === i ? { ...x, label: ev.target.value } : x)))}
                style={{ ...inputStyle, flex: '0 1 180px' }}
              />
              <input
                placeholder="内容"
                value={e.value}
                onChange={(ev) => setExtra((prev) => prev.map((x, j) => (j === i ? { ...x, value: ev.target.value } : x)))}
                style={{ ...inputStyle, flex: '1 1 240px' }}
              />
              <button type="button" className="txn-link" onClick={() => setExtra((prev) => prev.filter((_, j) => j !== i))}>
                −
              </button>
            </div>
          ))}
          <button type="button" className="txn-link" onClick={() => setExtra((prev) => [...prev, { label: '', value: '' }])}>
            ＋ 項目を追加
          </button>
        </div>

        {/* ---- 変更履歴(登録・編集ログ) ---- */}
        {operator && (
          <div className="gray-box">
            <div className="box-title">変更履歴</div>
            {history === null ? (
              <div className="hint">読み込み中…</div>
            ) : historyError ? (
              <div className="hint">変更履歴を読み込めませんでした。</div>
            ) : history.length === 0 ? (
              <div className="hint">変更履歴はまだありません。</div>
            ) : (
              <table className="no-wrap-table" style={{ fontSize: 12.5 }}>
                <thead>
                  <tr>
                    <th>日時</th>
                    <th>変更者</th>
                    <th>内容</th>
                  </tr>
                </thead>
                <tbody>
                  {history.map((h) => (
                    <tr key={h.id}>
                      <td>{fmtTs(h.at)}</td>
                      <td>{nameOf(h.by)}</td>
                      <td style={{ whiteSpace: 'normal' }}>{auditText(h)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>
        )}

        <div className="modal-actions">
          <button className="btn-green" type="submit" disabled={busy || !numbering}>
            {busy ? '保存中…' : isNew ? '登録する' : '保存する'}
          </button>
          <button className="btn-line" type="button" onClick={onClose}>
            キャンセル
          </button>
          {!isNew && (
            <button className="btn-line danger" type="button" disabled={busy} onClick={() => setConfirmDelete(true)}>
              削除
            </button>
          )}
        </div>
      </form>

      {confirmDelete && operator && (
        <Modal onClose={() => setConfirmDelete(false)} width="min(420px, 94vw)" showClose={false}>
          <h3>削除しますか?</h3>
          <p className="hint" style={{ margin: '10px 0' }}>
            {operator.name}({operator.op_code})を完全に削除します。取り消せません。
            <br />
            退職した場合は削除ではなく、在籍を「退職」に変更してください。
          </p>
          <div className="modal-actions">
            <button className="btn-line" onClick={() => setConfirmDelete(false)}>
              キャンセル
            </button>
            <button className="btn-sm-ng" disabled={busy} onClick={() => void handleDelete()}>
              完全に削除する
            </button>
          </div>
        </Modal>
      )}
    </Modal>
  );
}
